import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { pill, LABEL, MUTED, TEXTAREA, LINK } from '../lib/equipment';
import {
  STATUS_TONE, Swatch, AuthedImg, useAuthedUrl, downloadAuthed, PlacementPreview, fmtBytes, fmtTs, num,
} from '../components/TechPackParts';
import '../components/techpacks.css';
// @@TECH_PACK_THUMBS_v97@@ previews: file tiles ?thumb=sm (480 px), location art + placement ?thumb=md (1200 px); “Get” downloads the original

// @@TECH_PACKS_v94@@ HQ › Tech Packs › one pack: details, print style, mockups,
// print locations (art, placement, inks), share link, review lifecycle, history.
// Content is editable only in draft | changes_requested — the server enforces it
// (409 `locked`); "Revise" reopens a sent/approved pack as a new revision.
const JSON_HEADERS = { 'Content-Type': 'application/json' };
// @@TECH_PACK_LABELS_v112@@ optional 4x6 label pages on the PDF (?labels=…; &only=1 = labels-only PDF).
// The pick is a per-browser convenience (localStorage, try/catch) — default all three.
const LABEL_KINDS = [['specs', 'Tech specs'], ['packing', 'Packing list'], ['preview', 'Preview / mockup']];
const LABEL_PICK_KEY = 'hq.techpack.labels';
function loadLabelPick() {
  try {
    const v = JSON.parse(window.localStorage.getItem(LABEL_PICK_KEY) || 'null');
    if (Array.isArray(v)) return LABEL_KINDS.map(([k]) => k).filter((k) => v.includes(k));
  } catch (e) { /* storage blocked */ }
  return LABEL_KINDS.map(([k]) => k);
}
async function readJson(r) { try { return await r.json(); } catch (e) { return {}; } }
// Typical mesh ranges per technique / specialty (guidance only).
const MESH_HINT = {
  spot: 'Spot: 110–160 for solids and underbase, 180–230 for fine detail.',
  simulated_process: 'Sim process: 156–200 underbase, 230–305 for halftone colors.',
  cmyk: 'CMYK: 305–355 on tight, well-tensioned screens.',
  index: 'Index: 200–230.',
  halftone: 'Halftone: 200–305 depending on LPI.',
  other: '',
};
const SPEC_HINT = { puff: 'Puff: 60–86.', high_density: 'High density: 60–110 with a thick stencil.', glitter: 'Glitter: 25–60 (coarse).', metallic: 'Metallic: 86–110.', discharge: '' };
// @@TECH_PACKS_DISCHARGE_v95@@ discharge printing: settings form, blank compatibility, per-ink discharge flag.
const INK_HINT = {
  discharge: 'Discharge: 156–200 for colors, 110–156 for a discharge white underbase. Mix activator per batch — pot life is a few hours.',
  discharge_hybrid: 'Discharge underbase 110–156; plastisol top colors 156–230 printed wet-on-wet or after a flash.',
  water_based: 'Water-based: 156–230; keep screens wet, avoid long press stops.',
};
const isDischargeInk = (k) => k === 'discharge' || k === 'discharge_hybrid';
const s0 = (v) => (v == null ? '' : String(v));
function dischForm(d) {
  const x = d || {};
  return { base: x.base || '', activator_pct: s0(x.activator_pct), cure_temp_f: s0(x.cure_temp_f), dwell_sec: s0(x.dwell_sec),
    strike_off: !!x.strike_off, result_color: x.result_color || '', notes: x.notes || '' };
}
function dischDefaults(meta, cur) {
  const d = (meta && meta.discharge_defaults) || {};
  const empty = !cur.base && !cur.activator_pct && !cur.cure_temp_f && !cur.dwell_sec;
  return empty ? { ...cur, base: d.base || 'water_based', activator_pct: s0(d.activator_pct), cure_temp_f: s0(d.cure_temp_f), dwell_sec: s0(d.dwell_sec), strike_off: d.strike_off !== false } : cur;
}
function DischargeFields({ value, onChange, meta, title }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <div className="tp-span3 tp-discharge">
      <div className="tp-discharge-title">{title || 'Discharge settings'}</div>
      <div className="tp-form-grid tp-form-flat">
        <label><span style={LABEL}>Discharge base</span>
          <select className="hq-select tp-w100" value={value.base} onChange={set('base')}>
            <option value="">Not set</option>{((meta && meta.discharge_bases) || []).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label><span style={LABEL}>Activator (%)</span><input className="hq-input tp-w100" inputMode="decimal" value={value.activator_pct} placeholder="6" onChange={set('activator_pct')} />
          <span style={MUTED}>Typically 4–8% by weight (ZFS)</span></label>
        <label><span style={LABEL}>Cure</span>
          <span className="tp-row" style={{ gap: 6, flexWrap: 'nowrap' }}>
            <input className="hq-input" style={{ width: '50%', minWidth: 0 }} inputMode="numeric" value={value.cure_temp_f} placeholder="320" onChange={set('cure_temp_f')} aria-label="Cure temperature °F" />
            <input className="hq-input" style={{ width: '50%', minWidth: 0 }} inputMode="numeric" value={value.dwell_sec} placeholder="120" onChange={set('dwell_sec')} aria-label="Dryer dwell seconds" />
          </span>
          <span style={MUTED}>°F · seconds in the dryer</span></label>
        <label className="tp-span2"><span style={LABEL}>Expected garment color after discharge</span>
          <input className="hq-input tp-w100" value={value.result_color} maxLength={120} placeholder="e.g. Black discharges to a warm tan" onChange={set('result_color')} /></label>
        <label className="tp-switch" style={{ alignSelf: 'end', paddingBottom: 8 }}>
          <input type="checkbox" checked={value.strike_off} onChange={set('strike_off')} /><span>Strike-off / test print required</span></label>
        <label className="tp-span3"><span style={LABEL}>Discharge notes</span>
          <input className="hq-input tp-w100" value={value.notes} maxLength={1000} placeholder="Ventilation, dye-lot testing, pigment add-ins…" onChange={set('notes')} /></label>
      </div>
    </div>
  );
}

function packForm(p) {
  return {
    title: p.title || '', client_id: p.client_id || '', client_style_code: p.client_style_code || '',
    blank_source: p.blank.source || 'custom', blank_name: p.blank.name || '', blank_brand: p.blank.brand || '',
    blank_style: p.blank.style || '', blank_color: p.blank.color || '', blank_fabric: p.blank.fabric || '',
    sizes: p.sizes || '', quantity: p.quantity == null ? '' : String(p.quantity), due_date: p.due_date || '',
    print_ink: p.print_ink, print_technique: p.print_technique, specialty: p.specialty || [],
    mesh_default: p.mesh_default == null ? '' : String(p.mesh_default), notes: p.notes || '', internal_notes: p.internal_notes || '',
    blank_dischargeable: p.blank.dischargeable || 'unknown', discharge: dischForm(p.discharge),
  };
}
function locForm(l) {
  return {
    preset: l.preset, name: l.name, side: l.side,
    width_in: l.width_in == null ? '' : String(l.width_in), height_in: l.height_in == null ? '' : String(l.height_in),
    ref_point: l.ref_point, from_ref_in: l.from_ref_in == null ? '' : String(l.from_ref_in),
    offset_x_in: l.offset_x_in == null ? '' : String(l.offset_x_in),
    placement_notes: l.placement_notes || '', grading: l.grading || '', notes: l.notes || '',
    inherit_style: l.inherit_style, print_ink: l.print_ink || 'plastisol', print_technique: l.print_technique || 'spot',
    specialty: l.specialty || [], mesh_default: l.mesh_default == null ? '' : String(l.mesh_default),
    discharge: dischForm(l.discharge),
    colors: (l.colors || []).map((c) => ({ ...c, pantone: c.pantone || '', name: c.name || '', hex: c.hex || '', mesh: c.mesh == null ? '' : String(c.mesh), notes: c.notes || '' })),
  };
}

export default function TechPackDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = !!user && ['admin', 'manager', 'staff'].includes(user.role);
  const canManage = !!user && ['admin', 'manager'].includes(user.role);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [meta, setMeta] = useState(null);
  const [clients, setClients] = useState([]);
  const [blanks, setBlanks] = useState({ available: false, blanks: [] });
  const [inks, setInks] = useState([]);
  const [busy, setBusy] = useState('');
  const [labelOpen, setLabelOpen] = useState(false); // @@TECH_PACK_LABELS_v112@@
  const [labelPick, setLabelPick] = useState(loadLabelPick);

  const load = useCallback(() => {
    apiFetch('/api/techpacks/' + id).then(async (r) => {
      const d = await readJson(r);
      if (r.ok) { setData(d); setErr(''); } else setErr(d.error || (r.status === 404 ? 'Tech pack not found.' : 'Could not load the tech pack.'));
    }).catch(() => setErr('Could not load the tech pack.'));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiFetch('/api/techpacks/meta').then(readJson).then(setMeta).catch(() => {});
    apiFetch('/api/techpacks/clients').then(readJson).then((d) => setClients(d.clients || [])).catch(() => {});
    apiFetch('/api/techpacks/blanks').then(readJson).then((d) => setBlanks(d && d.blanks ? d : { available: false, blanks: [] })).catch(() => {});
    apiFetch('/api/techpacks/inks').then(readJson).then((d) => setInks(d.inks || [])).catch(() => {});
  }, []);

  // Every mutation returns the full detail; one helper applies it.
  const call = useCallback(async (path, opts, okMsg) => {
    const r = await apiFetch('/api/techpacks/' + id + path, opts);
    const d = await readJson(r);
    if (!r.ok) { setMsg(d.error || 'Something went wrong.'); return null; }
    if (d.pack) setData(d);
    setMsg(okMsg || '');
    return d;
  }, [id]);
  const post = (path, body, okMsg) => call(path, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) }, okMsg);

  if (err) return <div className="hq-page-content"><p className="hq-msg">{err}</p><Link to="/tech-packs" style={LINK}>← Tech packs</Link></div>;
  if (!data || !meta) return <div className="hq-page-content"><div className="hq-empty">Loading…</div></div>;
  const p = data.pack;
  const editable = canWrite && p.editable;

  async function download(kind, qs, key) {
    setBusy(key || kind);
    try { await downloadAuthed(`/api/techpacks/${id}/${kind}${qs || ''}`); } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  function togglePick(k) {
    const next = LABEL_KINDS.map(([x]) => x).filter((x) => (x === k ? !labelPick.includes(x) : labelPick.includes(x)));
    setLabelPick(next);
    try { window.localStorage.setItem(LABEL_PICK_KEY, JSON.stringify(next)); } catch (e) { /* storage blocked */ }
  }
  async function act(path, body, confirmText, okMsg) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(path);
    await post(path, body, okMsg);
    setBusy('');
  }
  async function del() {
    if (!window.confirm(`Delete ${p.code} and every attached file? This can't be undone.`)) return;
    const r = await apiFetch('/api/techpacks/' + id, { method: 'DELETE' });
    if (r.ok) navigate('/tech-packs'); else setMsg('Could not delete.');
  }
  async function duplicate() {
    setBusy('dup');
    const d = await post('/duplicate', {});
    setBusy('');
    if (d && d.pack) navigate('/tech-packs/' + d.pack.id);
  }
  const client = clients.find((c) => c.id === p.client_id);

  return (
    <div className="hq-page-content tp-detail">
      <div style={{ marginBottom: 10 }}><Link to="/tech-packs" style={LINK}>← Tech packs</Link></div>
      <div className="tp-head">
        <div style={{ minWidth: 0 }}>
          <div className="tp-mono" style={{ color: 'var(--hq-muted)' }}>{p.code} · Revision {p.revision}{p.archived_at ? ' · Archived' : ''}</div>
          <h1 className="tp-h1">{p.title}</h1>
          <div className="tp-row" style={{ gap: 8 }}>
            <span style={pill(STATUS_TONE[p.status])}>{p.status_label}</span>
            {p.client_name && <span style={MUTED}>{p.client_name}{p.client_style_code ? ` · Style ${p.client_style_code}` : ''}</span>}
            {p.decided_name && <span style={MUTED}>· {p.status === 'approved' ? 'Approved' : 'Changes requested'} by {p.decided_name} {fmtTs(p.decided_at)}</span>}
          </div>
        </div>
        <div className="tp-actions">
          <button type="button" className="hq-btn hq-btn-ghost" disabled={!!busy} onClick={() => download('pdf')}>{busy === 'pdf' ? 'Building…' : 'PDF'}</button>
          <button type="button" className={'hq-btn hq-btn-ghost' + (labelOpen ? ' tp-btn-on' : '')} aria-expanded={labelOpen} onClick={() => setLabelOpen(!labelOpen)}>4×6 labels</button>
          <button type="button" className="hq-btn hq-btn-ghost" disabled={!!busy} onClick={() => download('zip')}>{busy === 'zip' ? 'Zipping…' : 'Download all'}</button>
          {canWrite && p.editable && <button type="button" className="hq-btn" disabled={!!busy || !p.locations.length}
            title={!p.locations.length ? 'Add a print location first' : ''}
            onClick={() => act('/send', {}, p.client_id ? `Send ${p.code} to ${p.client_name} for review? It locks for editing until you revise it.` : 'Mark ready for review? (No client is attached, so nobody gets notified — approve it here when ready.)', p.client_id ? 'Sent to the client portal for review.' : 'Marked ready for review.')}>
            {p.status === 'changes_requested' ? `Send rev ${p.revision + 1}` : 'Send for review'}</button>}
          {canManage && p.status === 'review' && <button type="button" className="hq-btn" disabled={!!busy}
            onClick={() => { const n = window.prompt('Approve on behalf of the client — who approved it? (name)', p.client_name ? '' : (user.first_name || '') + ' ' + (user.last_name || '')); if (n !== null) act('/approve', { name: n }, null, 'Approved.'); }}>Mark approved</button>}
          {canWrite && ['review', 'approved'].includes(p.status) && <button type="button" className="hq-btn hq-btn-ghost" disabled={!!busy}
            onClick={() => act('/revise', {}, `Reopen ${p.code} as revision ${p.revision + 1}? ${p.status === 'approved' ? 'The current approval will be cleared and the client will need to approve again.' : 'The client can’t respond until you send it again.'}`, `Reopened as revision ${p.revision + 1}.`)}>Revise</button>}
        </div>
      </div>
      {labelOpen && (
        <div className="tp-labels">
          <div className="tp-labels-head">
            <strong>4×6 label pages</strong>
            <span style={MUTED}>Tech specs and packing list print black-only for a thermal printer; the preview keeps color.</span>
          </div>
          <div className="tp-labels-row">
            {LABEL_KINDS.map(([k, t]) => (
              <label key={k} className="tp-labels-opt"><input type="checkbox" checked={labelPick.includes(k)} onChange={() => togglePick(k)} /> {t}</label>
            ))}
          </div>
          <div className="tp-labels-row">
            <button type="button" className="hq-btn" disabled={!!busy || !labelPick.length}
              onClick={() => download('pdf', '?labels=' + labelPick.join(','), 'pdf-labels')}>{busy === 'pdf-labels' ? 'Building…' : 'PDF + labels'}</button>
            <button type="button" className="hq-btn hq-btn-ghost" disabled={!!busy || !labelPick.length}
              onClick={() => download('pdf', '?only=1&labels=' + labelPick.join(','), 'labels-only')}>{busy === 'labels-only' ? 'Building…' : 'Labels only (4×6)'}</button>
            {!labelPick.length && <span style={MUTED}>Pick at least one label.</span>}
          </div>
        </div>
      )}
      {msg && <p className="hq-msg" style={{ marginTop: 0 }}>{msg}</p>}
      {!p.editable && (
        <div className="tp-banner">
          {p.status === 'approved' ? 'Approved — locked so production prints exactly what the client signed off on.' : 'Sent for review — locked while the client looks at it.'}
          {canWrite && ' Click “Revise” to change it as a new revision.'}
        </div>
      )}
      {p.status === 'changes_requested' && data.history[0] && data.history[0].to_status === 'changes_requested' && (
        <div className="tp-banner tp-banner-red"><strong>{data.history[0].actor}:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{data.history[0].note}</span></div>
      )}

      <div className="tp-cols">
        <div className="tp-main">
          <DetailsPanel p={p} meta={meta} clients={clients} blanks={blanks} editable={editable} onSave={(body) => call('', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(body) }, 'Saved.')} />
          <FilesPanel title="Mockups" hint="PNG or JPG — front / back / colorway renders. These go on the PDF cover." kind="mockup" accept="image/png,image/jpeg"
            packId={id} files={p.mockups} editable={editable} onDone={setData} onMsg={setMsg} maxMb={meta.max_upload_mb} call={call} />
          <div className="tp-section-head">
            <h2>Print locations <span style={MUTED}>({p.locations.length})</span></h2>
            {editable && <AddLocation presets={meta.presets} onAdd={(preset) => post('/locations', { preset }, 'Location added.')} />}
          </div>
          {p.locations.length === 0 && <div className="hq-panel"><div className="hq-empty">No print locations yet. Add one — full front, left chest, back, sleeves, custom…</div></div>}
          {p.locations.map((l, i) => (
            <LocationCard key={l.id} packId={id} pack={p} loc={l} index={i} count={p.locations.length} meta={meta} inks={inks}
              editable={editable} call={call} onMsg={setMsg} onDone={setData}
              onMove={(dir) => {
                const ids = p.locations.map((x) => x.id);
                const j = i + dir; if (j < 0 || j >= ids.length) return;
                [ids[i], ids[j]] = [ids[j], ids[i]];
                post('/locations/reorder', { ids });
              }} />
          ))}
          <FilesPanel title="Reference files" hint="Client briefs, brand guides, original art — anything else the printer should have. Included in the download." kind="reference"
            packId={id} files={p.references} editable={editable} onDone={setData} onMsg={setMsg} maxMb={meta.max_upload_mb} call={call} />
        </div>

        <div className="tp-side">
          <SharePanel p={p} canWrite={canWrite} post={post} />
          <div className="hq-panel">
            <div className="hq-panel-head"><h2>Client review</h2></div>
            <div style={{ padding: 16, fontSize: 13, lineHeight: 1.55 }}>
              {!p.client_id && <p style={{ margin: 0, color: 'var(--hq-muted)' }}>No client attached — set one in Details so they can review and approve it in their portal.</p>}
              {p.client_id && client && client.portal_users === 0 && <p style={{ margin: 0, color: '#e8c15a' }}>{p.client_name} has no portal login yet, so they can’t see this until a contact is invited.</p>}
              {p.client_id && (!client || client.portal_users > 0) && <p style={{ margin: 0, color: 'var(--hq-muted)' }}>After “Send for review”, {p.client_name} sees it under <strong>Tech Packs</strong> in their portal and approves it (typed name) or requests changes. You get a task + email either way.</p>}
            </div>
          </div>
          <InternalNotes p={p} canWrite={canWrite} call={call} />
          <HistoryPanel history={data.history} onNote={(note) => post('/note', { note })} />
          {canWrite && (
            <div className="hq-panel">
              <div style={{ padding: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button type="button" className="hq-btn hq-btn-ghost" disabled={!!busy} onClick={duplicate}>Duplicate</button>
                <button type="button" className="hq-btn hq-btn-ghost" disabled={!!busy} onClick={() => act('/archive', { archived: !p.archived_at }, null, p.archived_at ? 'Restored.' : 'Archived.')}>{p.archived_at ? 'Restore' : 'Archive'}</button>
                {canManage && <button type="button" className="hq-btn hq-btn-ghost" style={{ color: '#ff7a73' }} onClick={del}>Delete</button>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Details + print style ───────────────────────────────────────────────────
function DetailsPanel({ p, meta, clients, blanks, editable, onSave }) {
  const [f, setF] = useState(() => packForm(p));
  const [saving, setSaving] = useState(false);
  const base = useRef(JSON.stringify(packForm(p)));
  // Only reset when the SERVER copy changed — another panel's save must not wipe unsaved edits here.
  useEffect(() => { const nf = packForm(p); const s = JSON.stringify(nf); if (s !== base.current) { base.current = s; setF(nf); } }, [p]);
  const dirty = JSON.stringify(f) !== base.current;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const client = clients.find((c) => c.id === f.client_id);
  const toggleSpec = (k) => setF({ ...f, specialty: f.specialty.includes(k) ? f.specialty.filter((x) => x !== k) : [...f.specialty, k] });
  function pickBlank(name) {
    if (!name) { setF({ ...f, blank_source: 'custom' }); return; }
    const b = blanks.blanks.find((x) => x.name === name);
    if (!b) return;
    setF({ ...f, blank_source: 'catalog', blank_name: b.name, blank_brand: b.brand || '', blank_fabric: [b.weight, b.fabric].filter(Boolean).join(' · '),
      blank_dischargeable: b.discharge_available ? 'yes' : 'no' });
  }
  const hint = [INK_HINT[f.print_ink], MESH_HINT[f.print_technique], ...f.specialty.map((s) => SPEC_HINT[s]).filter(Boolean)].filter(Boolean).join(' ');
  // Discharge settings show when the pack's ink system is discharge, or any location prints a discharge screen.
  const showDischarge = isDischargeInk(f.print_ink) || p.uses_discharge;
  const dis = !editable;
  return (
    <form className="hq-panel" onSubmit={async (e) => { e.preventDefault(); setSaving(true); await onSave(f); setSaving(false); }}>
      <div className="hq-panel-head"><h2>Details</h2>
        {editable && <button className="hq-btn" type="submit" disabled={!dirty || saving}>{saving ? 'Saving…' : dirty ? 'Save details' : 'Saved'}</button>}</div>
      <fieldset disabled={dis} className="tp-fieldset">
        <div className="tp-form-grid">
          <label className="tp-span2"><span style={LABEL}>Title</span><input className="hq-input tp-w100" value={f.title} maxLength={160} onChange={set('title')} /></label>
          <label><span style={LABEL}>Client</span>
            <select className="hq-select tp-w100" value={f.client_id} onChange={set('client_id')}>
              <option value="">No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span style={MUTED}>{client ? (client.designation_code ? `ID prefix ${client.designation_code}-${p.number}` : 'Client has no designation code — ID stays ' + p.number) : 'ID ' + p.number}</span>
          </label>
          <label><span style={LABEL}>Client style # / SKU</span><input className="hq-input tp-w100" value={f.client_style_code} maxLength={60} placeholder="their internal code" onChange={set('client_style_code')} /></label>
        </div>
        <div className="tp-sub">Blank</div>
        <div className="tp-form-grid">
          <label className="tp-span2"><span style={LABEL}>Preferred blank {blanks.available ? '(Quick Quote catalog)' : ''}</span>
            <select className="hq-select tp-w100" value={f.blank_source === 'catalog' ? f.blank_name : ''} onChange={(e) => pickBlank(e.target.value)}>
              <option value="">{blanks.available ? 'Custom / not in catalog — type below' : 'Catalog unavailable — type below'}</option>
              {blanks.blanks.map((b) => <option key={b.name} value={b.name}>{[b.brand, b.name].filter(Boolean).join(' — ')}</option>)}
            </select></label>
          <label><span style={LABEL}>Brand</span><input className="hq-input tp-w100" value={f.blank_brand} placeholder="Comfort Colors" onChange={(e) => setF({ ...f, blank_brand: e.target.value, blank_source: 'custom' })} /></label>
          <label><span style={LABEL}>Style #</span><input className="hq-input tp-w100" value={f.blank_style} placeholder="1717" onChange={set('blank_style')} /></label>
          <label><span style={LABEL}>Product</span><input className="hq-input tp-w100" value={f.blank_name} placeholder="Heavyweight Tee" onChange={(e) => setF({ ...f, blank_name: e.target.value, blank_source: 'custom' })} /></label>
          <label><span style={LABEL}>Garment color</span><input className="hq-input tp-w100" value={f.blank_color} placeholder="Pepper" onChange={set('blank_color')} /></label>
          <label className="tp-span2"><span style={LABEL}>Fabric / weight</span><input className="hq-input tp-w100" value={f.blank_fabric} placeholder="6.1 oz · 100% ring-spun cotton" onChange={set('blank_fabric')} /></label>
          <label><span style={LABEL}>Sizes</span><input className="hq-input tp-w100" value={f.sizes} placeholder="S–3XL" onChange={set('sizes')} /></label>
          <label><span style={LABEL}>Quantity</span><input className="hq-input tp-w100" inputMode="numeric" value={f.quantity} onChange={set('quantity')} /></label>
          <label><span style={LABEL}>Due date</span><input className="hq-input tp-w100" type="date" value={f.due_date} onChange={set('due_date')} /></label>
          <label><span style={LABEL}>Dischargeable?</span>
            <select className="hq-select tp-w100" value={f.blank_dischargeable} onChange={set('blank_dischargeable')}>
              <option value="unknown">Not confirmed</option><option value="yes">Yes — dischargeable</option><option value="no">No</option></select>
            <span style={MUTED}>{f.blank_source === 'catalog' ? 'From the catalog’s Discharge Available column' : 'Discharge needs high-cotton, reactive-dyed garments'}</span></label>
        </div>
        <div className="tp-sub">Print style <span style={MUTED}>— every location inherits this unless it overrides</span></div>
        <div className="tp-form-grid">
          <label><span style={LABEL}>Ink system</span>
            <select className="hq-select tp-w100" value={f.print_ink}
              onChange={(e) => { const v = e.target.value; setF({ ...f, print_ink: v, discharge: isDischargeInk(v) ? dischDefaults(meta, f.discharge) : f.discharge }); }}>
              {meta.inks.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          <label><span style={LABEL}>Technique</span>
            <select className="hq-select tp-w100" value={f.print_technique} onChange={set('print_technique')}>{meta.techniques.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          <label><span style={LABEL}>Preferred mesh</span>
            <select className="hq-select tp-w100" value={f.mesh_default} onChange={set('mesh_default')}>
              <option value="">Printer’s choice</option>{meta.meshes.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
          <div className="tp-span3"><span style={LABEL}>Specialty</span>
            <div className="tp-chips">{meta.specialties.map(([k, l]) => (
              <button type="button" key={k} className={f.specialty.includes(k) ? 'tp-chip tp-chip-on' : 'tp-chip'} onClick={() => toggleSpec(k)}>{l}</button>))}</div>
          </div>
          {hint && <div className="tp-span3" style={MUTED}>Mesh guide — {hint}</div>}
          {showDischarge && <DischargeFields value={f.discharge} meta={meta} onChange={(d) => setF({ ...f, discharge: d })}
            title={isDischargeInk(f.print_ink) ? 'Discharge settings' : 'Discharge settings — used by the discharge screens in your locations'} />}
          {showDischarge && f.blank_dischargeable !== 'yes' && (
            <div className={f.blank_dischargeable === 'no' ? 'tp-span3 tp-banner tp-banner-red' : 'tp-span3 tp-banner'} style={{ marginBottom: 0 }}>
              {f.blank_dischargeable === 'no'
                ? 'This blank is marked NOT dischargeable — discharge won’t lift the dye. Pick a dischargeable blank or change the print style.'
                : 'Confirm the blank is dischargeable (high-cotton, reactive-dyed) and run a strike-off before production.'}
            </div>
          )}
        </div>
        <div className="tp-sub">Production notes <span style={MUTED}>— shown to the client and on the share link / PDF</span></div>
        <div style={{ padding: '0 20px 20px' }}>
          <textarea style={TEXTAREA} value={f.notes} maxLength={4000} placeholder="Cure temp, print order notes, fold / bag / tag instructions…" onChange={set('notes')} />
        </div>
      </fieldset>
    </form>
  );
}

function InternalNotes({ p, canWrite, call }) {
  const [v, setV] = useState(p.internal_notes || '');
  useEffect(() => { setV(p.internal_notes || ''); }, [p.internal_notes]);
  const dirty = v !== (p.internal_notes || '');
  return (
    <div className="hq-panel">
      <div className="hq-panel-head"><h2>Internal notes</h2>
        {canWrite && dirty && <button type="button" className="hq-btn" onClick={() => call('/internal-notes', { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ internal_notes: v }) }, 'Saved.')}>Save</button>}</div>
      <div style={{ padding: 16 }}>
        <textarea style={{ ...TEXTAREA, minHeight: 70 }} value={v} disabled={!canWrite} maxLength={4000} placeholder="HQ only — never shown to clients or printers." onChange={(e) => setV(e.target.value)} />
      </div>
    </div>
  );
}

function SharePanel({ p, canWrite, post }) {
  const url = p.share_token ? `${window.location.origin}/public/techpack/${p.share_token}` : '';
  const [copied, setCopied] = useState(false);
  return (
    <div className="hq-panel">
      <div className="hq-panel-head"><h2>Share link</h2>
        {canWrite && (
          <label className="tp-switch">
            <input type="checkbox" checked={!!p.share_token} onChange={(e) => post('/share', { enabled: e.target.checked }, e.target.checked ? 'Share link on.' : 'Share link off — the old link no longer works.')} />
            <span>{p.share_token ? 'On' : 'Off'}</span>
          </label>
        )}</div>
      <div style={{ padding: 16, fontSize: 13 }}>
        {!url && <p style={{ margin: 0, color: 'var(--hq-muted)' }}>One link for printers / vendors: a read-only page with every art file, mockup and the PDF — plus “Download all” as a single .zip. No login needed.</p>}
        {url && (
          <>
            <input className="hq-input tp-w100" readOnly value={url} onFocus={(e) => e.target.select()} style={{ fontSize: 12 }} />
            <div className="tp-row" style={{ marginTop: 10, gap: 8 }}>
              <button type="button" className="hq-btn" onClick={() => { navigator.clipboard && navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {}); }}>{copied ? 'Copied' : 'Copy link'}</button>
              <a className="hq-btn hq-btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} href={url} target="_blank" rel="noreferrer">Open</a>
              {canWrite && <button type="button" className="hq-linkish" onClick={() => { if (window.confirm('Make a new link? The current link stops working.')) post('/share', { enabled: true, regenerate: true }, 'New link made — the old one no longer works.'); }}>New link</button>}
            </div>
            <p style={{ ...MUTED, marginTop: 10, marginBottom: 0 }}>Anyone with this link can view and download the files. Turn it off to kill the link.</p>
          </>
        )}
      </div>
    </div>
  );
}

function HistoryPanel({ history, onNote }) {
  const [note, setNote] = useState('');
  const LBL = { draft: 'Draft', review: 'Sent for review', approved: 'Approved', changes_requested: 'Changes requested' };
  return (
    <div className="hq-panel">
      <div className="hq-panel-head"><h2>History</h2></div>
      <form style={{ padding: '12px 16px 0', display: 'flex', gap: 8 }} onSubmit={async (e) => { e.preventDefault(); if (!note.trim()) return; await onNote(note); setNote(''); }}>
        <input className="hq-input" style={{ flex: 1, minWidth: 0 }} placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} />
        <button className="hq-btn hq-btn-ghost" type="submit" disabled={!note.trim()}>Add</button>
      </form>
      <ul className="tp-history">
        {history.map((h) => (
          <li key={h.id}>
            <div style={{ fontSize: 13 }}>
              {h.from_status !== h.to_status && h.to_status ? <strong>{LBL[h.to_status] || h.to_status}{h.revision ? ` · rev ${h.revision}` : ''}</strong> : null}
              {h.note && <div style={{ whiteSpace: 'pre-wrap' }}>{h.note}</div>}
            </div>
            <div style={MUTED}>{h.actor || '—'} · {fmtTs(h.created_at)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AddLocation({ presets, onAdd }) {
  const [v, setV] = useState('full_front');
  return (
    <div className="tp-row" style={{ gap: 8 }}>
      <select className="hq-select" value={v} onChange={(e) => setV(e.target.value)}>
        {presets.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
      </select>
      <button type="button" className="hq-btn" onClick={() => onAdd(v)}>Add location</button>
    </div>
  );
}

// ── Files (mockups / references / location art) ────────────────────────────
async function uploadFiles(packId, list, { kind, locationId, maxMb, onDone, onMsg }) {
  for (const file of list) {
    if (file.size > maxMb * 1048576) { onMsg(`${file.name} is larger than ${maxMb} MB.`); continue; }
    const qs = new URLSearchParams({ kind });
    if (locationId) qs.set('location_id', locationId);
    onMsg(`Uploading ${file.name}…`);
    const r = await apiFetch(`/api/techpacks/${packId}/files?${qs}`, {
      method: 'POST', headers: { 'Content-Type': 'application/octet-stream', 'X-Filename': encodeURIComponent(file.name) }, body: file,
    });
    const d = await readJson(r);
    if (!r.ok) { onMsg(d.error || `Could not upload ${file.name}.`); return; }
    onDone(d);
  }
  onMsg('');
}
function FileTile({ packId, f, editable, call }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="tp-file">
      <div className="tp-file-thumb">
        {f.is_image
          ? <AuthedImg path={`/api/techpacks/${packId}/files/${f.id}?thumb=sm`} alt={f.filename} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <span className="tp-ext">{(f.filename.split('.').pop() || 'file').slice(0, 5).toUpperCase()}</span>}
      </div>
      <div className="tp-file-name" title={f.filename}>{f.label || f.filename}</div>
      <div className="tp-row" style={{ justifyContent: 'space-between', gap: 6 }}>
        <span style={MUTED}>{fmtBytes(f.bytes)}</span>
        <span>
          <button type="button" className="hq-linkish" style={{ fontSize: 12 }} disabled={busy}
            onClick={async () => { setBusy(true); try { await downloadAuthed(`/api/techpacks/${packId}/files/${f.id}`, f.filename); } catch (e) { /* shown nowhere */ } setBusy(false); }}>Get</button>
          {editable && <button type="button" className="hq-link-danger" style={{ fontSize: 12, marginLeft: 8 }}
            onClick={() => { if (window.confirm(`Remove ${f.filename}?`)) call(`/files/${f.id}`, { method: 'DELETE' }, 'File removed.'); }}>Remove</button>}
        </span>
      </div>
    </div>
  );
}
function DropZone({ accept, onFiles, label }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div className={over ? 'tp-drop tp-drop-on' : 'tp-drop'} onClick={() => ref.current && ref.current.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files.length) onFiles(Array.from(e.dataTransfer.files)); }}>
      <input ref={ref} type="file" multiple accept={accept} style={{ display: 'none' }} onChange={(e) => { const l = Array.from(e.target.files || []); e.target.value = ''; if (l.length) onFiles(l); }} />
      <span>{label || 'Drop files or click to upload'}</span>
    </div>
  );
}
function FilesPanel({ title, hint, kind, accept, packId, files, editable, onDone, onMsg, maxMb, call }) {
  return (
    <div className="hq-panel">
      <div className="hq-panel-head"><h2>{title} <span style={MUTED}>({files.length})</span></h2></div>
      <div style={{ padding: 16 }}>
        <p style={{ ...MUTED, marginTop: 0 }}>{hint}</p>
        <div className="tp-files">
          {files.map((f) => <FileTile key={f.id} packId={packId} f={f} editable={editable} call={call} />)}
          {editable && <DropZone accept={accept} onFiles={(l) => uploadFiles(packId, l, { kind, maxMb, onDone, onMsg })} />}
        </div>
        {!editable && files.length === 0 && <div style={MUTED}>None.</div>}
      </div>
    </div>
  );
}

// ── One print location ──────────────────────────────────────────────────────
function LocationCard({ packId, pack, loc, index, count, meta, inks, editable, call, onMsg, onDone, onMove }) {
  const [f, setF] = useState(() => locForm(loc));
  const base = useRef(JSON.stringify(locForm(loc)));
  const [open, setOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => { const nf = locForm(loc); const s = JSON.stringify(nf); if (s !== base.current) { base.current = s; setF(nf); } }, [loc]);
  const dirty = JSON.stringify(f) !== base.current;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const art = loc.files.find((x) => x.is_image);
  const artUrl = useAuthedUrl(art ? `/api/techpacks/${packId}/files/${art.id}?thumb=md` : '');
  const inkByPantone = useMemo(() => Object.fromEntries(inks.filter((k) => k.pantone).map((k) => [k.pantone.toLowerCase(), k])), [inks]);
  const inkByName = useMemo(() => Object.fromEntries(inks.map((k) => [k.name.toLowerCase(), k])), [inks]);
  const effMesh = f.inherit_style ? pack.mesh_default : (f.mesh_default || pack.mesh_default);
  const presetDef = meta.presets.find((x) => x.key === f.preset);

  function applyPreset(key) {
    const d = meta.presets.find((x) => x.key === key);
    if (!d) return;
    const s = (v) => (v == null ? '' : String(v));
    setF({ ...f, preset: key, name: key === 'custom' ? f.name : d.name, side: d.side, ref_point: d.ref_point,
      from_ref_in: s(d.from_ref_in), offset_x_in: s(d.offset_x_in), width_in: s(d.width_in), height_in: s(d.height_in) });
  }
  function setColor(i, patch) {
    const colors = f.colors.map((c, j) => (j === i ? { ...c, ...patch } : c));
    setF({ ...f, colors });
  }
  function pantoneBlur(i) {
    const c = f.colors[i];
    const key = String(c.pantone || '').replace(/^(pantone|pms)\s*/i, '').trim().toLowerCase().replace(/(\d)\s*c$/, '$1 c');
    const hit = inkByPantone[key];
    if (hit) setColor(i, { pantone: hit.pantone, hex: c.hex || hit.hex || '', name: c.name || hit.name });
  }
  function nameBlur(i) {
    const c = f.colors[i];
    const hit = !c.pantone && inkByName[String(c.name || '').trim().toLowerCase()];
    if (hit) setColor(i, { pantone: hit.pantone || '', hex: c.hex || hit.hex || '' });
  }
  function moveColor(i, d) {
    const j = i + d; if (j < 0 || j >= f.colors.length) return;
    const colors = [...f.colors]; [colors[i], colors[j]] = [colors[j], colors[i]]; setF({ ...f, colors });
  }
  async function save() {
    setSaving(true);
    await call(`/locations/${loc.id}`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(f) }, `${f.name} saved.`);
    setSaving(false);
  }
  const preview = { ...f, width_in: f.width_in, height_in: f.height_in };
  const dis = !editable;

  return (
    <div className="hq-panel tp-loc">
      <div className="hq-panel-head tp-loc-head">
        <button type="button" className="tp-loc-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className="tp-loc-num">{String(index + 1).padStart(2, '0')}</span>
          <span style={{ minWidth: 0 }}>
            <span className="tp-loc-name">{f.name || 'Untitled location'}</span>
            <span style={MUTED}>{f.width_in && f.height_in ? `${num(f.width_in)}″ × ${num(f.height_in)}″ · ` : ''}{loc.effective.text} · {f.colors.length} ink{f.colors.length === 1 ? '' : 's'}</span>
          </span>
          <span className="tp-swatches">{f.colors.slice(0, 8).map((c, i) => <Swatch key={i} hex={c.hex} size={14} />)}</span>
        </button>
        <div className="tp-row" style={{ gap: 6 }}>
          {dirty && <span style={{ ...MUTED, color: '#e8c15a' }}>Unsaved</span>}
          {editable && <>
            <button type="button" className="tp-icon" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Move up">↑</button>
            <button type="button" className="tp-icon" disabled={index === count - 1} onClick={() => onMove(1)} aria-label="Move down">↓</button>
            <button type="button" className="hq-btn" disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
          </>}
        </div>
      </div>
      {open && (
        <fieldset disabled={dis} className="tp-fieldset">
          <div className="tp-loc-body">
            <div className="tp-loc-form">
              <div className="tp-form-grid tp-form-tight">
                <label><span style={LABEL}>Preset</span>
                  <select className="hq-select tp-w100" value={f.preset} onChange={(e) => applyPreset(e.target.value)}>
                    {meta.presets.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}</select></label>
                <label><span style={LABEL}>Location name</span><input className="hq-input tp-w100" value={f.name} maxLength={80} onChange={set('name')} /></label>
                <label><span style={LABEL}>Garment view</span>
                  <select className="hq-select tp-w100" value={f.side} onChange={set('side')}>
                    <option value="front">Front</option><option value="back">Back</option><option value="sleeve">Sleeve</option><option value="other">Other (label, hat, bag…)</option></select></label>
                <label><span style={LABEL}>Print width (in)</span><input className="hq-input tp-w100" inputMode="decimal" value={f.width_in} onChange={set('width_in')} /></label>
                <label><span style={LABEL}>Print height (in)</span><input className="hq-input tp-w100" inputMode="decimal" value={f.height_in} onChange={set('height_in')} /></label>
                <label><span style={LABEL}>Measured from</span>
                  <select className="hq-select tp-w100" value={f.ref_point} onChange={set('ref_point')}>
                    {meta.refs.map(([k, l]) => <option key={k} value={k}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}</select></label>
                <label><span style={LABEL}>Distance (in)</span><input className="hq-input tp-w100" inputMode="decimal" value={f.from_ref_in} disabled={dis || ['center', 'custom'].includes(f.ref_point)} onChange={set('from_ref_in')} /></label>
                <label><span style={LABEL}>Offset from center (in)</span><input className="hq-input tp-w100" inputMode="decimal" value={f.offset_x_in} placeholder="0 = centered" onChange={set('offset_x_in')} />
                  <span style={MUTED}>+ toward wearer’s left, − right</span></label>
                <label className="tp-span3"><span style={LABEL}>Placement notes</span><input className="hq-input tp-w100" value={f.placement_notes} maxLength={1000} placeholder={presetDef && presetDef.key === 'left_chest' ? 'e.g. align top of art with 2nd button' : 'anything the measurements don’t cover'} onChange={set('placement_notes')} /></label>
                <label className="tp-span3"><span style={LABEL}>Size grading</span><input className="hq-input tp-w100" value={f.grading} maxLength={1000} placeholder="e.g. Youth & S: 10″ wide · 2XL+: 13″ wide" onChange={set('grading')} /></label>
              </div>
            </div>
            <div className="tp-loc-preview">
              <PlacementPreview loc={preview} artUrl={artUrl} height={250} dark />
              <div style={{ ...MUTED, textAlign: 'center', marginTop: 4 }}>{loc.placement_text || 'Set size + placement'}</div>
            </div>
          </div>

          <div className="tp-sub">Print style</div>
          <div className="tp-form-grid tp-form-tight">
            <label className="tp-switch tp-span3">
              <input type="checkbox" checked={f.inherit_style} onChange={(e) => setF({ ...f, inherit_style: e.target.checked })} />
              <span>Inherit from pack — <span style={MUTED}>{pack.style_text}{pack.mesh_default ? ` · ${pack.mesh_default} mesh` : ''}</span></span>
            </label>
            {!f.inherit_style && <>
              <label><span style={LABEL}>Ink system</span>
                <select className="hq-select tp-w100" value={f.print_ink}
                  onChange={(e) => { const v = e.target.value; setF({ ...f, print_ink: v, discharge: isDischargeInk(v) ? dischDefaults(meta, f.discharge) : f.discharge }); }}>
                  {meta.inks.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
              <label><span style={LABEL}>Technique</span>
                <select className="hq-select tp-w100" value={f.print_technique} onChange={set('print_technique')}>{meta.techniques.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
              <label><span style={LABEL}>Mesh</span>
                <select className="hq-select tp-w100" value={f.mesh_default} onChange={set('mesh_default')}>
                  <option value="">Same as pack</option>{meta.meshes.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
              <div className="tp-span3"><div className="tp-chips">{meta.specialties.map(([k, l]) => (
                <button type="button" key={k} className={f.specialty.includes(k) ? 'tp-chip tp-chip-on' : 'tp-chip'}
                  onClick={() => setF({ ...f, specialty: f.specialty.includes(k) ? f.specialty.filter((x) => x !== k) : [...f.specialty, k] })}>{l}</button>))}</div></div>
              {isDischargeInk(f.print_ink) && <DischargeFields value={f.discharge} meta={meta} onChange={(d) => setF({ ...f, discharge: d })} title="Discharge settings — this location" />}
            </>}
            {(f.inherit_style || !isDischargeInk(f.print_ink)) && f.colors.some((c) => c.discharge) && (
              <div className="tp-span3" style={MUTED}>
                Discharge screens here use the pack’s discharge settings{pack.discharge_text ? ` — ${pack.discharge_text}` : ' (set them under Details › Print style)'}.
              </div>
            )}
          </div>

          <div className="tp-sub">Inks <span style={MUTED}>— top to bottom = print order. Type a Pantone (e.g. 186 C) and it fills from the library.</span></div>
          <div style={{ padding: '0 20px', overflowX: 'auto' }}>
            <table className="tp-inks">
              <thead><tr><th>#</th><th>Swatch</th><th>Pantone</th><th>Name</th><th>Mesh</th><th title="Print a white underbase under this color">Underbase</th><th title="This screen is discharge ink — e.g. a discharge white underbase or a pigmented discharge color">Discharge</th><th title="Flash cure after this color">Flash</th><th>Notes</th><th /></tr></thead>
              <tbody>
                {f.colors.map((c, i) => (
                  <tr key={i}>
                    <td className="tp-mono">{i + 1}</td>
                    <td><input type="color" className="tp-color" value={c.hex || '#ffffff'} onChange={(e) => setColor(i, { hex: e.target.value.toUpperCase() })} /></td>
                    <td><input className="hq-input" style={{ width: 120 }} list="tp-pantones" value={c.pantone} placeholder="e.g. 186 C" onChange={(e) => setColor(i, { pantone: e.target.value })} onBlur={() => pantoneBlur(i)} /></td>
                    <td><input className="hq-input" style={{ width: 150 }} list="tp-inknames" value={c.name} placeholder="name" onChange={(e) => setColor(i, { name: e.target.value })} onBlur={() => nameBlur(i)} /></td>
                    <td><select className="hq-select" value={c.mesh} onChange={(e) => setColor(i, { mesh: e.target.value })}>
                      <option value="">{effMesh ? `${effMesh} (default)` : '—'}</option>{meta.meshes.map((m) => <option key={m} value={m}>{m}</option>)}</select></td>
                    <td style={{ textAlign: 'center' }}><input type="checkbox" checked={!!c.underbase} onChange={(e) => setColor(i, { underbase: e.target.checked })} /></td>
                    <td style={{ textAlign: 'center' }}><input type="checkbox" checked={!!c.discharge} onChange={(e) => setColor(i, { discharge: e.target.checked })} /></td>
                    <td style={{ textAlign: 'center' }}><input type="checkbox" checked={!!c.flash} onChange={(e) => setColor(i, { flash: e.target.checked })} /></td>
                    <td><input className="hq-input" style={{ width: 160 }} value={c.notes} maxLength={200} onChange={(e) => setColor(i, { notes: e.target.value })} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {editable && <>
                        <button type="button" className="tp-icon" disabled={i === 0} onClick={() => moveColor(i, -1)} aria-label="Up">↑</button>
                        <button type="button" className="tp-icon" disabled={i === f.colors.length - 1} onClick={() => moveColor(i, 1)} aria-label="Down">↓</button>
                        <button type="button" className="tp-icon tp-icon-x" onClick={() => setF({ ...f, colors: f.colors.filter((_, j) => j !== i) })} aria-label="Remove">×</button>
                      </>}
                    </td>
                  </tr>
                ))}
                {f.colors.length === 0 && <tr><td colSpan={10} style={{ ...MUTED, padding: '10px 4px' }}>No inks yet.</td></tr>}
              </tbody>
            </table>
            <datalist id="tp-pantones">{inks.filter((k) => k.pantone).map((k) => <option key={k.id} value={k.pantone}>{k.name}</option>)}</datalist>
            <datalist id="tp-inknames">{inks.map((k) => <option key={k.id} value={k.name} />)}</datalist>
            {editable && f.colors.length < 16 && <button type="button" className="hq-btn hq-btn-ghost" style={{ marginTop: 10 }}
              onClick={() => setF({ ...f, colors: [...f.colors, { pantone: '', name: '', hex: '', mesh: '', underbase: false, discharge: isDischargeInk(f.inherit_style ? pack.print_ink : f.print_ink), flash: false, notes: '' }] })}>+ Add ink</button>}
          </div>

          <div className="tp-form-grid tp-form-tight" style={{ paddingTop: 16 }}>
            <label className="tp-span3"><span style={LABEL}>Location notes</span>
              <textarea style={{ ...TEXTAREA, minHeight: 60 }} value={f.notes} maxLength={2000} onChange={set('notes')} /></label>
          </div>

        </fieldset>
      )}
      {open && (
        <>
          <div className="tp-sub">Art files <span style={MUTED}>— AI / EPS / PDF / PSD / PNG… Attach a PNG or JPG proof too: it previews here, on the garment diagram and in the PDF.</span></div>
          <div style={{ padding: '0 20px 20px' }}>
            <div className="tp-files">
              {loc.files.map((x) => <FileTile key={x.id} packId={packId} f={x} editable={editable} call={call} />)}
              {editable && <DropZone onFiles={(l) => uploadFiles(packId, l, { kind: 'art', locationId: loc.id, maxMb: meta.max_upload_mb, onDone, onMsg })} />}
            </div>
            {editable && (
              <div style={{ marginTop: 14, textAlign: 'right' }}>
                <button type="button" className="hq-link-danger" onClick={() => { if (window.confirm(`Remove ${loc.name} and its art files?`)) call(`/locations/${loc.id}`, { method: 'DELETE' }, 'Location removed.'); }}>Remove location</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

